import { create } from "zustand";

import type { EventAccess } from "@/types/eventAccess";
import type { OrganisationMemberRole } from "@/types/organisation";
import { readStoredUser } from "@/Utils/Auth/authSession";
import { loggerService, LogTag } from "@/Utils/LoggerService";

export interface Organisation {
  org_id: number;
  name: string;
  slug: string;
  owner_id?: number;
  role?: OrganisationMemberRole;
  company_number?: string;
  vat_number?: string;
  email?: string;
  phone?: string;
  website?: string | null;
  signature_image_path?: string | null;
  signature_image_data_url?: string | null;
  owner_function?: string | null;
  volunteer_reimbursement_max?: number | null;
  volunteer_reimbursement_max_annual?: number | null;
  volunteer_km_rate?: number | null;
  address_street?: string;
  address_number?: string;
  address_zip?: string;
  address_city?: string;
  address_country?: string;
  created_at?: string;
  updated_at?: string;
}

export interface OrganisationMember {
  user_id: number;
  email: string;
  full_name: string | null;
  role: OrganisationMemberRole;
  joined_at: string;
  event_access: EventAccess;
  event_ids: number[];
  /** Subset of `event_ids` granted with the `manage` access level. */
  manage_event_ids: number[];
}

interface OrganisationState {
  organisation: Organisation | null;
  organisations: Organisation[];
  setOrganisation: (org: Organisation) => void;
  setOrganisations: (orgs: Organisation[]) => void;
  switchOrganisation: (orgId: number) => void;
  clearOrganisation: () => void;
}

const storedOrg = readStoredUser()?.organisation ?? null;

export const useOrganisationStore = create<OrganisationState>((set, get) => ({
  organisation: storedOrg ? { ...storedOrg } : null,
  organisations: [],

  setOrganisation: (org) => {
    loggerService.info(
      LogTag.STORE,
      "OrganisationStore: setOrganisation",
      org.slug,
    );
    set({ organisation: org });
  },

  setOrganisations: (orgs) => {
    loggerService.info(
      LogTag.STORE,
      "OrganisationStore: setOrganisations",
      `count=${orgs.length}`,
    );
    set({ organisations: orgs });
  },

  switchOrganisation: (orgId) => {
    const found = get().organisations.find((o) => o.org_id === orgId) ?? null;
    loggerService.info(
      LogTag.STORE,
      "OrganisationStore: switchOrganisation",
      found?.slug,
    );
    set({ organisation: found });
  },

  clearOrganisation: () => {
    loggerService.info(LogTag.STORE, "OrganisationStore: clearOrganisation");
    set({ organisation: null, organisations: [] });
  },
}));
